import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { useToggle } from 'react-use';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import {
  getColumnNameFromCustomColumnId,
  getCustomColumnId,
} from 'src/utils/columns/reportV2CustomColumnUtils';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import { ReportColumn } from 'src/WebApiController';

import { REPORT_TYPE_TO_CUSTOM_COLUMN_USER_SETTING_ID } from './ReportsTableEditorForm.constants';

export type UseReportsCustomColumnProps = {
  reportType: ReportTypesV2;
};

export const useReportsCustomColumn = ({
  reportType,
}: UseReportsCustomColumnProps) => {
  const [isCustomColumnModalOpen, toggleCustomColumnModal] = useToggle(false);
  const [customColumnToEdit, setCustomColumnToEdit] = useState<ReportColumn>();
  const [customColumnNameToDelete, setCustomColumnNameToDelete] =
    useState<string>();

  const {
    value: customReportColumns = [],
    setUserSetting: setCustomReportColumns,
  } = useServerUserSetting<ReportColumn[]>({
    id: REPORT_TYPE_TO_CUSTOM_COLUMN_USER_SETTING_ID[reportType],
  });

  const deleteCustomColumnConfirmDialog = useBasicDialog();

  const onEditCustomColumn = useCallback(
    (columnId: string) => {
      if (!isCustomColumnModalOpen) {
        const existingCustomColumn = customReportColumns.find(
          (c) => c.columnName === columnId
        );
        if (existingCustomColumn) {
          setCustomColumnToEdit(existingCustomColumn);
          toggleCustomColumnModal();
        }
      }
    },
    [customReportColumns, isCustomColumnModalOpen, toggleCustomColumnModal]
  );

  const onDeleteCustomColumn = useCallback(
    (columnId: string) => {
      if (!isCustomColumnModalOpen) {
        const existingCustomColumn = customReportColumns.find(
          (c) => c.columnName === columnId
        );
        if (existingCustomColumn) {
          setCustomColumnNameToDelete(existingCustomColumn.columnName);
          deleteCustomColumnConfirmDialog.launchDialog();
        }
      }
    },
    [
      customReportColumns,
      deleteCustomColumnConfirmDialog,
      isCustomColumnModalOpen,
    ]
  );

  const onCreateCustomColumn = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (!isCustomColumnModalOpen) {
        setCustomColumnToEdit(undefined);
        toggleCustomColumnModal();
      }
    },
    [isCustomColumnModalOpen, setCustomColumnToEdit, toggleCustomColumnModal]
  );

  const onUpsertCustomColumnSubmit = useCallback(
    (columnId: string, displayName: string, columnValue: string) => {
      let hasExistingCustomColumn = false;
      const newCustomReportColumns = customReportColumns.map((c) => {
        // Update existing custom column if it's there
        if (getCustomColumnId(c.columnName) === columnId) {
          hasExistingCustomColumn = true;
          return {
            ...c,
            columnName: getColumnNameFromCustomColumnId(columnId, displayName),
            columnValue: columnValue,
          };
        } else {
          return c;
        }
      });

      // If the custom column doesn't exist, add it
      if (!hasExistingCustomColumn) {
        newCustomReportColumns.push({
          columnName: getColumnNameFromCustomColumnId(columnId, displayName),
          columnValue: columnValue,
        });
      }

      setCustomReportColumns(newCustomReportColumns);
    },
    [customReportColumns, setCustomReportColumns]
  );

  const onDeleteCustomColumnSubmit = useCallback(
    (columnName: string) => {
      const newCustomReportColumns = customReportColumns.filter(
        (c) => c.columnName !== columnName
      );
      setCustomReportColumns(newCustomReportColumns);
    },
    [customReportColumns, setCustomReportColumns]
  );

  // Without this memoization, the customReportColumns array
  // will be recreated on every render, causing unnecessary re-renders in components that depend on it.
  const customReportColumnsMemoized = useMemo(
    () => customReportColumns,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(customReportColumns)]
  );

  return {
    isCustomColumnModalOpen,
    toggleCustomColumnModal,
    customColumnToEdit,
    customColumnNameToDelete,
    setCustomColumnNameToDelete,
    onEditCustomColumn,
    onDeleteCustomColumn,
    onCreateCustomColumn,
    onUpsertCustomColumnSubmit,
    onDeleteCustomColumnSubmit,
    deleteCustomColumnConfirmDialog,
    customReportColumns: customReportColumnsMemoized,
  };
};
